<script setup lang="ts">
import { computed } from 'vue'
import CheckpointStrategyList from './CheckpointStrategyList.vue'
import SlidePanel from '@/components/SlidePanel.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'
import type { UdlGuideline } from './udl_index_types'

const PRINCIPLE_PANEL_CLASSES: Record<string, string> = {
  Representation: 'bg-[rgb(253,250,253)] before:bg-[rgb(253,250,253)]',
  'Action & Expression': 'bg-[rgb(242,247,252)] before:bg-[rgb(242,247,252)]',
  Engagement: 'bg-[rgb(247,255,245)] before:bg-[rgb(247,255,245)]'
}

const props = withDefaults(
  defineProps<{
    guideline: UdlGuideline
    principle: string
    expanded?: boolean
    expandedCheckpoints?: Record<string, boolean>
  }>(),
  { expanded: true, expandedCheckpoints: () => ({}) }
)

const emit = defineEmits<{
  'update:expanded': [guidelineLabel: string, expanded: boolean]
  'update:checkpointExpanded': [checkpointRef: string, expanded: boolean]
}>()

const visibilityStore = useVisibilityStore()

const panelId = computed(() => `udl-guideline-${props.guideline.label.replace(/\W+/g, '-').toLowerCase()}`)
const panelClass = computed(() => PRINCIPLE_PANEL_CLASSES[props.principle] || '')
const checkpointPrinciple = computed(() => (visibilityStore.editorVisibility ? props.principle : ''))

function toggleExpanded() {
  emit('update:expanded', props.guideline.label, !props.expanded)
}

function isCheckpointExpanded(checkpointRef: string) {
  return props.expandedCheckpoints[checkpointRef] !== false
}
</script>

<template>
  <div>
    <h4 class="my-2.5 leading-[1.1]">
      <button
        type="button"
        class="mt-2 px-1.5 py-px text-left text-text-xl font-regular capitalize text-gray-700 hover:underline focus:outline-offset-4"
        :aria-expanded="expanded"
        :aria-controls="panelId"
        @click="toggleExpanded"
      >
        <span aria-hidden="true">{{ expanded ? '–' : '+' }}</span>
        {{ guideline.label }}
      </button>
    </h4>

    <SlidePanel :expanded="expanded" :notch-inset="9">
      <div
        :id="panelId"
        data-testid="guideline-panel"
        :class="panelClass"
        class="relative rounded-xs border border-gray-400 p-4 text-text-md text-gray-900 before:absolute before:left-[10%] before:-top-[0.5px] before:size-3 before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:border-l before:border-t before:border-gray-400"
      >
        <ul class="list-none">
          <li v-for="checkpoint in guideline.checkpoints" :key="checkpoint.standard_long_ref">
            <CheckpointStrategyList
              :checkpoint="checkpoint"
              :principle="checkpointPrinciple"
              :expanded="isCheckpointExpanded(checkpoint.standard_long_ref)"
              @update:expanded="(checkpointRef, checkpointExpanded) => emit('update:checkpointExpanded', checkpointRef, checkpointExpanded)"
            />
          </li>
        </ul>
      </div>
    </SlidePanel>
  </div>
</template>
